import { EventContext } from '@/redux/modules/analytics';
import {
    getAccountCreatedDate,
    getAuthToken,
    getSignInProviderIsSocial,
    isUserLoggedIn,
} from '@/redux/modules/account/user/user.selectors';
import { removeAuthCookie, setAuthCookie } from '@/redux/modules/cookies';
import { SocialAuthErrorKey, SocialAuthErrorValue, SocialAuthMessage, SocialAuthType } from '@/types/auth/socialAuth';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Cookies from 'universal-cookie';
import getExpirationFromToken from '@/utils/getExpirationFromToken';
import usePrevious from '@liveauctioneers/caterwaul-components/lib/hooks/usePrevious';

const Cookie = new Cookies();

const Auth = () => {
    const dispatch = useAppDispatch();
    const authToken = useAppSelector(getAuthToken);
    const userLoggedIn = useAppSelector(isUserLoggedIn);
    const prevAuthToken = usePrevious(authToken);
    const prevUserLoggedIn = usePrevious(userLoggedIn);
    const authCookie = Cookie.get('bidder-auth');
    const [queryParams] = useSearchParams();

    // Social auth data
    const socialLoginErrorParam = queryParams.get(SocialAuthErrorKey.Error);
    const isSocialAuth = useAppSelector(getSignInProviderIsSocial);
    const accountCreatedDate = useAppSelector(getAccountCreatedDate);

    useEffect(() => {
        if (authToken && authToken !== prevAuthToken) {
            const expires = getExpirationFromToken(authToken);
            dispatch(setAuthCookie(expires, authToken));
        } else if (!userLoggedIn && authCookie) {
            dispatch(removeAuthCookie());
        }
    }, [authCookie, authToken, dispatch, prevAuthToken, prevUserLoggedIn, userLoggedIn]);

    // Handle social auth popup login messages. This will close the current page popup and send a message back to the main window
    // This is handled in the FacebookAuth, GoogleAuth and AppleAuth components
    useEffect(() => {
        // Handle errors
        if (socialLoginErrorParam !== null && window.opener && authToken === '') {
            // Handle account already exists
            if (socialLoginErrorParam === SocialAuthErrorValue.AlreadyExists) {
                window.opener.postMessage(
                    {
                        authToken,
                        message: EventContext.social_register_already_exists,
                        success: false,
                        type: SocialAuthType.SocialAuth,
                    },
                    window.location.origin
                );
                // Handle cancel clicked from facebook auth popup
            } else if (socialLoginErrorParam.includes(SocialAuthErrorValue.CannotFetchToken)) {
                window.opener.postMessage(
                    {
                        authToken,
                        message: SocialAuthMessage.Canceled,
                        success: false,
                        type: SocialAuthType.SocialAuth,
                    },
                    window.location.origin
                );
            }
            // Handle success
        } else if (userLoggedIn && isSocialAuth && authToken && window.opener) {
            window.opener.postMessage(
                {
                    authToken,
                    message: SocialAuthMessage.Success,
                    success: true,
                    type: SocialAuthType.SocialAuth,
                },
                window.location.origin
            );
        }
    }, [userLoggedIn, dispatch, accountCreatedDate, authToken, socialLoginErrorParam, isSocialAuth]);

    return null;
};

export default Auth;
